import { faCalendarRange, faCircleQuestion } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useConfig } from '@nexelem/config';
import { Button, Calendar, DateRange, DropdownMenu, IconButton, Popover } from '@nexelem/components';
import { endOfYear, startOfYear } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useCustomTranslation } from '../../../../hooks';
import { formatCompactDateRange } from '../../../../utils';
import { useGanttFilterStore } from '../hooks/useGanttFilterStore';
import { useTimelineStore } from '../Gantt/store';
import { useGanttInstanceId } from '../Gantt/utils';

const MONTHS_SCALE_YEARS = [2025, 2026, 2027, 2028, 2029, 2030];

export const GanttFilterDate = () => {
  const { modules } = useConfig();
  const instanceId = useGanttInstanceId();
  const { tWrapper } = useCustomTranslation('common');
  const { from, to } = useGanttFilterStore((state) => state.ganttDateRange);
  const setDateRange = useGanttFilterStore((state) => state.setGanttDateRange);
  const resetDateRange = useGanttFilterStore((state) => state.reset);
  const measureType = useTimelineStore((state) => state.filters.current.measureType);
  const isMonthScale = measureType === 'months';
  const isMonthScaleEnabled = modules.production.ganttMonthScale;

  const [dateValue, setDateValue] = useState<DateRange>({ from, to });
  const [isOpen, setIsOpen] = useState(false);

  const handleDateChange = useCallback(() => {
    if (dateValue.from && dateValue.to) {
      setDateRange(instanceId, { from: dateValue.from, to: dateValue.to });
      setIsOpen(false);
    }
  }, [dateValue.from, dateValue.to, setDateRange, instanceId]);

  const isSelectedDataRangeYear = MONTHS_SCALE_YEARS.some(
    (year) =>
      startOfYear(new Date(`${year}-01-01`)).getTime() === from.getTime() &&
      endOfYear(new Date(`${year}-12-31`)).getTime() === to.getTime(),
  );

  // Change date range for months scale
  useEffect(() => {
    if (!isMonthScaleEnabled) {
      return;
    }

    if (isMonthScale && !isSelectedDataRangeYear) {
      setDateRange(instanceId, { from: startOfYear(new Date()), to: endOfYear(new Date()) });
    }

    if (!isMonthScale && isSelectedDataRangeYear) {
      const initialDateRange = useGanttFilterStore.getInitialState().ganttDateRange;
      resetDateRange();
      setDateValue({ from: initialDateRange.from, to: initialDateRange.to });
    }
  }, [isMonthScale, isMonthScaleEnabled, resetDateRange, setDateRange, isSelectedDataRangeYear, instanceId]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setDateValue({ from, to });
    }
  };

  if (isMonthScale) {
    return (
      <div className="flex items-center gap-2">
        <Button disabled={isMonthScale} StartIcon={<FontAwesomeIcon icon={faCalendarRange} />} variant="text">
          {formatCompactDateRange(from, to)}
        </Button>
        {MONTHS_SCALE_YEARS.map((year) => (
          <Button
            key={year}
            variant="text"
            onClick={() => {
              const start = startOfYear(new Date(`${year}-01-01`));
              const end = endOfYear(new Date(`${year}-12-31`));
              setDateRange(instanceId, { from: start, to: end });
            }}
          >
            {year}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger>
        <Button StartIcon={<FontAwesomeIcon icon={faCalendarRange} />} variant="text">
          {formatCompactDateRange(from, to)}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.CalendarContainer
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <Popover.Root>
              <Popover.Trigger>
                <IconButton Icon={<FontAwesomeIcon icon={faCircleQuestion} />} size="small" />
              </Popover.Trigger>
              <Popover.Content>{tWrapper('calendarMaxRange', { maxDays: 28 })}</Popover.Content>
            </Popover.Root>
            <Button className="self-end" disabled={!dateValue.from || !dateValue.to} onClick={handleDateChange}>
              {tWrapper('apply')}
            </Button>
          </div>
        }
      >
        <Calendar
          required
          resetOnSelect
          showWeekNumber
          defaultMonth={dateValue.from}
          max={28}
          mode="range"
          numberOfMonths={2}
          selected={dateValue}
          onSelect={setDateValue}
        />
      </DropdownMenu.CalendarContainer>
    </DropdownMenu.Root>
  );
};
